import { Container, Filter, Rectangle } from "pixi.js"
import { MushroomTripKind } from "game-common/item/mushroom_trip"
import { AnimatedFeedbackParams, EntityId } from "game-common/models"
import { SCREEN_FILTER_VERTEX } from "../night_sky"

/**
 * A mushroom's trip: for a few seconds after eating one, the eater's view of the world bends and changes colour, each
 * colour its own way (openspec ember/mushrooms). One full-screen pass over the world surface, last in its filters so a
 * night look is bent along with the ground it lights; the overlay (names, bubbles) and the GUI are not touched. The
 * filter is on the surface only while a trip runs.
 *
 * The shader works in WORLD ART PIXELS, from the surface's transform: in those units the stage's vertical stretch
 * cancels, so swirls and rings are round on screen, and each art pixel is moved by whole art pixels, so the art stays
 * crisp pixel art while it bends.
 *
 * Try one from the console: window.mushroomTrip("purple").
 */

const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#endif
varying vec2 vScreen;
uniform sampler2D uSampler;
uniform vec4 inputSize;
uniform vec4 outputFrame;
uniform vec4 inputClamp;

uniform vec2 uArt;
uniform vec2 uOrigin;
uniform vec2 uCenter;
uniform float uSnap;
uniform float uT;
uniform float uEnv;
uniform vec4 uSeed;
uniform vec4 uFreq;
uniform float uDir;
uniform float uWarp;
uniform float uSwirl;
uniform float uBreathe;
uniform float uRipple;
uniform float uMelt;
uniform float uPrism;
uniform float uHue;
uniform float uBands;
uniform float uTint;
uniform vec3 uTintColor;

const float TAU = 6.2831853;

vec3 hueShift(vec3 c, float a) {
    const vec3 k = vec3(0.57735);
    float ca = cos(a);
    return c * ca + cross(k, c) * sin(a) + k * dot(k, c) * (1.0 - ca);
}

// How far, in art px, the world drawn at screen point s is read from.
vec2 displacement(vec2 s) {
    vec2 d = (s - uCenter) / uArt;
    float r = length(d);
    float diag = length(outputFrame.zw / uArt);
    vec2 q = (s - outputFrame.xy) / uArt / diag;
    float t = uT, e = uEnv;
    vec2 disp = vec2(0.0);

    // Wobble: two drifting interference layers, a few waves across the screen.
    disp += uWarp * e * 2.6 * vec2(
        sin(q.y * 11.0 * uFreq.x + t * 1.7 + uSeed.x) + 0.6 * sin((q.x + q.y) * 17.0 * uFreq.y - t * 2.3 + uSeed.y),
        sin(q.x * 10.0 * uFreq.y - t * 1.5 + uSeed.z) + 0.6 * sin((q.x - q.y) * 15.0 * uFreq.z + t * 2.1 + uSeed.w));

    // Swirl round the eater, swinging back and forth.
    float fall = exp(-r * r / (2.0 * pow(diag * 0.2, 2.0)));
    float ang = uSwirl * e * fall * 0.6 * uDir * (0.35 + 0.65 * sin(t * 1.1 + uSeed.x));
    float ca = cos(ang), sa = sin(ang);
    disp += vec2(ca * d.x + sa * d.y, -sa * d.x + ca * d.y) - d;

    // Breathe: the world swells and shrinks round the eater.
    disp -= d * uBreathe * e * 0.04 * sin(t * TAU * 0.55 * uFreq.w);

    // Ripple: rings running out from the eater.
    disp += d / max(r, 1.0) * uRipple * e * 1.45 * sin(r / diag * 46.0 * uFreq.z - t * 6.0) * exp(-r / (diag * 0.45));

    // Melt: the world drips down in uneven columns (reading from above draws it lower).
    float h = 0.5 + 0.5 * sin(q.x * 31.0 * uFreq.x + uSeed.y) * sin(q.x * 83.0 * uFreq.y + uSeed.z);
    disp.y -= uMelt * e * (0.87 + 7.5 * h * h) * (0.55 + 0.45 * sin(t * 0.9 + h * 4.0));
    disp.x += uMelt * e * 0.72 * sin(q.y * 40.0 + t * 2.0);
    return disp;
}

// Mirrored past the screen's edge: a clamp smears the last row into streaks.
vec4 sampleAt(vec2 s) {
    vec2 w = outputFrame.zw;
    vec2 f = w - abs(w - mod(s - outputFrame.xy, 2.0 * w));
    return texture2D(uSampler, clamp(f * inputSize.zw, inputClamp.xy, inputClamp.zw));
}

void main(void) {
    vec2 s = vScreen;
    vec2 disp;
    if (uSnap > 0.5) {
        vec2 cell = (floor((s - uOrigin) / uArt) + 0.5) * uArt + uOrigin;
        disp = floor(displacement(cell) + 0.5);
    } else {
        disp = displacement(s);
    }
    vec2 from = s + disp * uArt;

    vec2 split = vec2(cos(uT * 0.9 + uSeed.w), sin(uT * 0.9 + uSeed.w)) * uPrism * uEnv * 1.45;
    if (uSnap > 0.5) split = floor(split + 0.5);
    split *= uArt;
    vec4 mid = sampleAt(from);
    vec3 c = vec3(sampleAt(from + split).r, mid.g, sampleAt(from - split).b);
    float a = mid.a;

    float diag = length(outputFrame.zw / uArt);
    float r = length((s - uCenter) / uArt);
    float hueA = uHue * uEnv * 0.7 * sin(uT * 0.8 + uSeed.z) + uBands * uEnv * 0.8 * sin(r / diag * 12.0 - uT * 2.4 + uSeed.y);
    c = hueShift(c, hueA);
    float lum = dot(c, vec3(0.299, 0.587, 0.114));
    c = mix(vec3(lum), c, 1.0 + 0.45 * uEnv * clamp(uHue + uBands, 0.0, 1.5));

    // The edges take the colour's tint, pulsing; from brightness, since green times red is only dark olive.
    float vig = smoothstep(0.18, 0.62, r / diag);
    float beat = 0.55 + 0.45 * pow(0.5 + 0.5 * sin(uT * TAU * 1.15), 3.0);
    float l2 = dot(c, vec3(0.299, 0.587, 0.114));
    c = mix(c, uTintColor * (l2 * 1.9 + 0.05 * a), clamp(vig * uTint * uEnv * beat, 0.0, 1.0));

    gl_FragColor = vec4(clamp(c, 0.0, a), a);
}`

/** How much of each part of a trip a bite shows. */
export interface TripWeights {
    warp: number
    swirl: number
    breathe: number
    ripple: number
    melt: number
    prism: number
    hue: number
    bands: number
    tint: number
}

/** Yellow glows, purple swirls, red melts. Tuned by eye in the trip lab against an Ember screenshot. */
export const TRIP_PRESETS: Record<MushroomTripKind, { weights: TripWeights; tintColor: [number, number, number] }> = {
    yellow: {
        weights: { warp: .35, swirl: 0, breathe: 1, ripple: .45, melt: 0, prism: .15, hue: .25, bands: 0, tint: .55 },
        tintColor: [1, .82, .35],
    },
    purple: {
        weights: { warp: .4, swirl: 1, breathe: .2, ripple: .3, melt: 0, prism: 1, hue: .6, bands: .8, tint: .35 },
        tintColor: [.75, .45, 1],
    },
    red: {
        weights: { warp: .6, swirl: 0, breathe: .6, ripple: 0, melt: 1, prism: .4, hue: .1, bands: 0, tint: .9 },
        tintColor: [1, .25, .2],
    },
}

/** The parts that move the world rather than recolour it: what reduced motion leaves out. */
export const TRIP_MOTION: (keyof TripWeights)[] = ["warp", "swirl", "breathe", "ripple", "melt", "prism"]

/** From the bite to settled again. */
export const TRIP_SECONDS = 6
const ONSET_SECONDS = .9
/** The last part of the trip, settling back. */
const RELEASE = .45

/** One bite's look: its colour's preset, jittered, with its own phases, frequencies and swirl direction. */
export interface TripBite {
    kind: MushroomTripKind
    weights: TripWeights
    tintColor: [number, number, number]
    seed: [number, number, number, number]
    freq: [number, number, number, number]
    dir: 1 | -1
}

export const rollBite = (kind: MushroomTripKind, random: () => number = Math.random, reducedMotion = false): TripBite => {
    const preset = TRIP_PRESETS[kind]
    const weights = { ...preset.weights }
    for (const part of Object.keys(weights) as (keyof TripWeights)[]) {
        weights[part] = reducedMotion && TRIP_MOTION.includes(part) ? 0 : weights[part] * (.75 + .5 * random())
    }
    const four = (f: () => number) => [f(), f(), f(), f()] as [number, number, number, number]
    return {
        kind,
        weights,
        tintColor: preset.tintColor,
        seed: four(() => random() * Math.PI * 2),
        freq: four(() => .8 + .45 * random()),
        dir: random() < .5 ? -1 : 1,
    }
}

/** The colour a trip feedback starts on this client: only the eater's own client starts one. */
export const tripFeedbackMushroom = (
    entityId: EntityId, localPlayerId: EntityId | undefined, params: AnimatedFeedbackParams | undefined,
): MushroomTripKind | undefined =>
    params?.type === "trip" && localPlayerId !== undefined && entityId === localPlayerId ? params.mushroom : undefined

const smooth = (a: number, b: number, x: number) => {
    const t = Math.min(1, Math.max(0, (x - a) / (b - a)))
    return t * t * (3 - 2 * t)
}
/** smoothstep's inverse on 0..1: where on the ease-in a level is. */
const unsmooth = (y: number) => .5 - Math.sin(Math.asin(1 - 2 * Math.min(1, Math.max(0, y))) / 3)
const mod = (x: number, m: number) => ((x % m) + m) % m

const prefersReducedMotion = () =>
    typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches

export class MushroomTripFilter extends Filter {
    constructor() {
        super(SCREEN_FILTER_VERTEX, FRAGMENT, {
            uArt: new Float32Array([2, 2]),
            uOrigin: new Float32Array([0, 0]),
            uCenter: new Float32Array([0, 0]),
            uSnap: 1,
            uT: 0,
            uEnv: 0,
            uSeed: new Float32Array(4),
            uFreq: new Float32Array([1, 1, 1, 1]),
            uDir: 1,
            uWarp: 0, uSwirl: 0, uBreathe: 0, uRipple: 0, uMelt: 0, uPrism: 0, uHue: 0, uBands: 0, uTint: 0,
            uTintColor: new Float32Array([1, 1, 1]),
        })
        this.padding = 0
    }
}

const UNIFORM: Record<keyof TripWeights, string> = {
    warp: "uWarp", swirl: "uSwirl", breathe: "uBreathe", ripple: "uRipple", melt: "uMelt", prism: "uPrism",
    hue: "uHue", bands: "uBands", tint: "uTint",
}

export interface MushroomTripOptions {
    random?: () => number
    reducedMotion?: () => boolean
    filter?: Filter
}

export class MushroomTrip {
    readonly filter: Filter
    private bite?: TripBite
    /** The animation clock runs from the first bite, so a second bite never makes the waves jump. */
    private firstMs = 0
    /** When the ease-in (re)started; a second bite moves it so the envelope carries on from where it was. */
    private rampFromMs = 0
    private endMs = 0
    private random: () => number
    private reducedMotion: () => boolean

    constructor(
        private surface: Container,
        private screen: Rectangle,
        private resolution: () => number,
        options: MushroomTripOptions = {},
    ) {
        this.filter = options.filter ?? new MushroomTripFilter()
        this.random = options.random ?? Math.random
        this.reducedMotion = options.reducedMotion ?? prefersReducedMotion
    }

    get running(): boolean {
        return !!this.bite
    }

    /** The bite this trip shows, while one runs. */
    get look(): TripBite | undefined {
        return this.bite
    }

    /** A bite. A second one while a trip runs keeps its look and holds it for a full trip from now. */
    start = (kind: MushroomTripKind, nowMs: number): void => {
        if (this.bite && nowMs < this.endMs) {
            this.rampFromMs = nowMs - ONSET_SECONDS * 1000 * unsmooth(this.envelope(nowMs))
            this.endMs = nowMs + TRIP_SECONDS * 1000
            return
        }
        this.bite = rollBite(kind, this.random, this.reducedMotion())
        this.firstMs = this.rampFromMs = nowMs
        this.endMs = nowMs + TRIP_SECONDS * 1000
    }

    /** How strongly the trip shows (0..1): eased in, held, settled back by its end. */
    envelope = (nowMs: number): number => {
        if (!this.bite) return 0
        const ramp = smooth(this.rampFromMs, this.rampFromMs + ONSET_SECONDS * 1000, nowMs)
        return ramp * (1 - smooth(this.endMs - TRIP_SECONDS * 1000 * RELEASE, this.endMs, nowMs))
    }

    /** Once a frame, after the light pass: `eater` is the eater on screen (global logical px). */
    update = (nowMs: number, eater?: { x: number; y: number }): void => {
        if (this.bite && nowMs >= this.endMs) this.bite = undefined
        if (!this.bite) {
            this.detach()
            return
        }
        this.attach()

        const u = this.filter.uniforms
        const world = this.surface.worldTransform
        const artX = Math.abs(world.a) || 1, artY = Math.abs(world.d) || 1
        u.uArt[0] = artX
        u.uArt[1] = artY
        u.uOrigin[0] = mod(world.tx, artX)
        u.uOrigin[1] = mod(world.ty, artY)
        u.uCenter[0] = eater?.x ?? this.screen.x + this.screen.width / 2
        u.uCenter[1] = eater?.y ?? this.screen.y + this.screen.height / 2
        u.uT = (nowMs - this.firstMs) / 1000
        u.uEnv = this.envelope(nowMs)
        u.uSeed.set(this.bite.seed)
        u.uFreq.set(this.bite.freq)
        u.uDir = this.bite.dir
        for (const part of Object.keys(UNIFORM) as (keyof TripWeights)[]) u[UNIFORM[part]] = this.bite.weights[part]
        u.uTint = Math.min(1, this.bite.weights.tint)
        u.uTintColor.set(this.bite.tintColor)
        // At the screen's own resolution: at pixi's default of 1 the world's pixel art would be resampled.
        this.filter.resolution = this.resolution()
    }

    /** Last in the surface's filters, over the whole screen. The night look adds and drops its own filter at dusk
     * and dawn, so this is re-checked every frame a trip runs. */
    private attach = () => {
        const filters = this.surface.filters ?? []
        if (filters[filters.length - 1] !== this.filter) {
            this.surface.filters = [...filters.filter(f => f !== this.filter), this.filter]
        }
        // The whole screen: the world's bounds are a thousand children to measure every frame.
        if (this.surface.filterArea !== this.screen) this.surface.filterArea = this.screen
    }

    private detach = () => {
        const filters = this.surface.filters
        if (!filters?.includes(this.filter)) return
        const rest = filters.filter(f => f !== this.filter)
        this.surface.filters = rest.length ? rest : null
        if (!rest.length) this.surface.filterArea = null
    }
}
